import { useCallback, useSyncExternalStore } from "react";

const readStored = (storageKey: string) =>
  typeof localStorage !== "undefined" &&
  localStorage.getItem(storageKey) === "true";

// 키별 구독자. 같은 키를 동시에 mount한 호출이 여럿이어도 한쪽 setter 로 전부 갱신되게 한다.
const listenersByKey = new Map<string, Set<() => void>>();

const subscribe = (storageKey: string, listener: () => void) => {
  const listeners = listenersByKey.get(storageKey) ?? new Set();
  listenersByKey.set(storageKey, listeners);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) listenersByKey.delete(storageKey);
  };
};

const notify = (storageKey: string) => {
  listenersByKey.get(storageKey)?.forEach((listener) => listener());
};

/**
 * localStorage 에 남는 on/off 설정. 베타 여부는 보지 않는다 — 베타 밖에서도 유지돼야 하는 설정용.
 * 베타 전용 설정은 이 위에 베타 게이트를 얹은 useBetaPreference 를 쓴다.
 *
 * 정본은 localStorage 하나다. 같은 키를 읽는 호출이 여럿이어도 각자 사본을 갖지 않고
 * 같은 값을 본다 (다른 탭과의 동기화는 하지 않는다).
 */
export const useStoredPreference = (storageKey: string) => {
  const enabled = useSyncExternalStore(
    useCallback(
      (listener: () => void) => subscribe(storageKey, listener),
      [storageKey],
    ),
    () => readStored(storageKey),
    () => false,
  );

  const setEnabled = useCallback(
    (nextEnabled: boolean) => {
      // 정본이 localStorage 하나라 저장할 곳이 없으면 아무 일도 하지 않는다 (브라우저 앱에선 없는 경로)
      if (typeof localStorage === "undefined") return;
      localStorage.setItem(storageKey, String(nextEnabled));
      notify(storageKey);
    },
    [storageKey],
  );

  return { enabled, setEnabled };
};
